import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { catchError, retry, throwError, timer } from 'rxjs';
import { AuthService } from '../services/auth.service';

/** Header HRMSApi reads/generates a correlation id from (see CorrelationIdMiddleware). */
const CORRELATION_ID_HEADER = 'X-Correlation-Id';

/** A short burst that trips the per-admin rate limit is expected to clear within a couple of
 * seconds (it's a sliding window, not a hard outage) — retry it transparently instead of
 * surfacing "Too many requests" for something the user did nothing wrong to cause. Capped low so a
 * genuinely sustained overload still surfaces as an error rather than hammering the API forever. */
const MAX_RATE_LIMIT_RETRIES = 3;

/**
 * Attaches the platform-admin Bearer token to every outgoing request and, on any
 * 401 from the API, clears the stored session and bounces to /login (there is no
 * refresh-token flow for this app - deliberately simple, v1).
 *
 * Also tags every request with a correlation id (observability Phase 1/4) so a failure here can be
 * found by the exact same id in HRMSApi's logs, and reports genuine failures (5xx/network) to
 * GlitchTip — 401 is expected session-expiry flow, not tracked as an error.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const correlationId = crypto.randomUUID();
  const token = authService.getToken();
  const authorizedReq = req.clone({
    setHeaders: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      [CORRELATION_ID_HEADER]: correlationId,
    },
  });

  return next(authorizedReq).pipe(
    retry({
      count: MAX_RATE_LIMIT_RETRIES,
      delay: (error: unknown, retryCount: number) => {
        if (!(error instanceof HttpErrorResponse) || error.status !== 429) {
          return throwError(() => error);
        }
        const retryAfterSeconds = Number(error.headers?.get('Retry-After'));
        const baseDelayMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
          ? retryAfterSeconds * 1000
          : 400 * retryCount; // no header — back off a little more on each attempt
        const jitterMs = Math.random() * 200;
        return timer(baseDelayMs + jitterMs);
      },
    }),
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 401) {
          authService.logout(false);
          router.navigate(['/login']);
        } else if (error.status === 0 || error.status >= 500) {
          Sentry.captureException(error, {
            tags: { correlationId, httpStatus: error.status },
            extra: { url: error.url },
          });
        }
      }
      return throwError(() => error);
    }),
  );
};
